/**
 * The Outcomes list. One row per outcome capability. Clicking a row shows it in
 * the detail rail.
 */
import { ChevronRight } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import type { OutcomeTree } from "../types";

interface OutcomesListProps {
  outcomes: OutcomeTree[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
  filter: string;
}

export const OutcomesList = ({
  outcomes,
  selectedId,
  onSelect,
  isLoading,
  filter,
}: OutcomesListProps) => (
  <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-brand-border bg-brand-card">
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {isLoading ? (
        <p className="py-8 text-center text-sm text-brand-muted">Loading…</p>
      ) : outcomes.length === 0 ? (
        <p className="py-8 text-center text-sm text-brand-muted">
          No outcomes found{filter ? ` matching “${filter}”` : ""}.
        </p>
      ) : (
        outcomes.map((outcome) => {
          const isSelected = outcome.id === selectedId;
          return (
            <button
              type="button"
              key={outcome.id}
              onClick={() => onSelect(outcome.id)}
              aria-current={isSelected}
              className={cn(
                "flex flex-none items-center gap-3.5 border-b border-gray-100 px-4 py-3.5 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-unlikely/40",
                isSelected
                  ? "bg-unlikely/[0.04] shadow-[inset_3px_0_0_0_#B11082]"
                  : "hover:bg-brand-primary/5"
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-semibold">
                  {outcome.title}
                </span>
                {outcome.description && (
                  <span className="truncate text-xs text-brand-muted">
                    {outcome.description}
                  </span>
                )}
              </span>

              <ChevronRight
                className={cn(
                  "h-4 w-4 flex-none",
                  isSelected ? "text-unlikely" : "text-gray-300"
                )}
                aria-hidden={true}
              />
            </button>
          );
        })
      )}
    </div>
  </div>
);
